import { FeatureType, KlavioEventPropertyKey, KlayvioEvents, RoleAssignment, TopicPermissionLevel, TopicRoleType } from '../../types';
import { LitElement, css, html } from 'lit';
import { addRoleForEntity, createRoleRequestFromRole, removeRoleForEntity } from '../../helpers/api/roles-api';
import { customElement, property } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import { detectMobileViewport } from '../../helpers/device';
import featuresInfo from '../../helpers/state/features-info';
import { logEventWithKlayvio } from '../../helpers/api/api';
import organizationInfo from '../../helpers/state/organization-info';
import topicData from '../../helpers/state/topic-data';
import { validateEmail } from '../../helpers/strings';
import { getMainPage } from '../../helpers/page-name';

@customElement('grant-access-component')
export class GrantAccessComponent extends LitElement {
    static amplitude_callsite: string = 'Grant Access Modal';

    @property({ type: Object })
    roles?: RoleAssignment[];



    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    private async handleSubmit(e: any) {
        const email = e.detail.value.toLowerCase().trim();
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (
                this.roles === undefined ||
                topicData.topicId === undefined ||
                email === undefined
            )
                return;
            if (email === '' || this.roles.map((r) => r.email).includes(email)) {
                getMainPage().errorToastElement.showToast(
                    'Email cannot be an empty string or a duplicate of an existing email'
                );
                return;
            }
            if (!validateEmail(email)) {
                getMainPage().errorToastElement.showToast('Invalid email format.');
                return;
            }
            if (!undefinedIfEmpty(e.detail.option)) {
                getMainPage().errorToastElement.showToast('Select access type.');
                return;
            }
            const new_access_level = undefinedIfEmpty(e.detail.option);
            if (new_access_level === 'Owner') {
                await addRoleForEntity(
                    GrantAccessComponent.amplitude_callsite,
                    topicData.topicId,
                    {
                        email,
                        level: { TopicPermissionLevelType: TopicPermissionLevel.OWNER },
                    }
                );
            } else {
                await addRoleForEntity(
                    GrantAccessComponent.amplitude_callsite,
                    topicData.topicId,
                    {
                        email,
                        level: { TopicPermissionLevelType: TopicPermissionLevel.WRITER },
                    }
                );
            }

            try {
                logEventWithKlayvio(KlayvioEvents.InvitedUserToMap, accountInfo.email, [
                    { key: KlavioEventPropertyKey.MapName, value: topicData.topic?.name ?? '' },
                    { key: KlavioEventPropertyKey.MapId, value: topicData.topic?._id.$oid ?? '' },
                    { key: KlavioEventPropertyKey.EditorLink, value: topicData.topic?.cc_account_admin_link ?? '' },
                    { key: KlavioEventPropertyKey.InvitedEmail, value: email}
                ]);
            } catch (e) {
                console.error(e);
            }

            getMainPage().errorToastElement.showToast('New access added');
            await this.dispatchEvent(new CustomEvent('updated', {}));
        }, 'An error has occurred.  Context: could not update the topic.');
        await this.requestUpdate();
    }

    private closeUpgradeComponent() {
        this.dispatchEvent(new CustomEvent('close-upgrade'));
    }

    private async removeAdmin(e: CustomEvent) {
        if (this.roles !== undefined && this.roles.length === 1) {
            getMainPage().errorToastElement.showToastAndThrow('Maps must have at least one admin.');
        }
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (this.roles === undefined || topicData.topicId === undefined)
                return;
            const role_to_delete = this.roles.filter((w) => w.email === e.detail.email);
            await removeRoleForEntity(
                GrantAccessComponent.amplitude_callsite,
                topicData.topicId,
                createRoleRequestFromRole(role_to_delete[0])
            );
            try {
                logEventWithKlayvio(KlayvioEvents.RemovedEditor, accountInfo.email, [
                    { key: KlavioEventPropertyKey.MapName, value: topicData.topic?.name ?? '' },
                    { key: KlavioEventPropertyKey.MapId, value: topicData.topic?._id.$oid ?? '' },
                    { key: KlavioEventPropertyKey.RemovedEmail, value: role_to_delete[0].email }
                ]);
            } catch (e) {
                console.error(e);
            }

            this.dispatchEvent(new CustomEvent('updated', {}));
        }, 'An error has occurred.  Context: could not update the account.');
        await this.requestUpdate();
    }

    render() {
        return html`
            ${featuresInfo.addMapEditorsEnabled ? html`
                <div class="grantAccessContent">
                    ${detectMobileViewport() ? '' : html`
                        <h2 id="grantAccessTitle">
                            <mwc-icon>person_add_outline</mwc-icon>
                            Grant Edit Access
                        </h2>`
                    }
                    <div style="margin-top: 2rem">
                        <form-input-access
                            type="topic"
                            placeholder="Add by email address"
                            @submit="${this.handleSubmit}"
                            ?isOwner=${topicData.isOwner}
                        >
                            Send Invite
                        </form-input-access>
                    </div>
                    <div class="innerBox">
                        ${this.roles
                            ?.filter(
                                (r) =>
                                    (r.role as TopicRoleType).TopicRoleType?.level ===
                                    TopicPermissionLevel.OWNER
                            )
                            .map(
                                (c) => html`
                                    <access-item
                                        email="${c.email}"
                                        @removed="${this.removeAdmin}"
                                        ?canDelete=${topicData.isOwner ?? false}
                                        accessLevel="Owner"
                                    >
                                    </access-item>
                                `
                            )}
                        ${this.roles
                            ?.filter(
                                (r) =>
                                    (r.role as TopicRoleType).TopicRoleType?.level ===
                                    TopicPermissionLevel.WRITER
                            )
                            .map(
                                (c) => html`
                                    <access-item
                                        email="${c.email}"
                                        @removed="${this.removeAdmin}"
                                        canDelete
                                        accessLevel="Editor"
                                    >
                                    </access-item>
                                `
                            )}
                    </div>
                    ${this.roles && this.roles?.length > 3 && !isDefined(organizationInfo.organization) ? html`
                        <div class="message">
                            <upgrade-cta
                                text="Woah, that's a lot of people! Maintain brand standards, assign roles, and co-manage all of your maps with our Teams plan."
                                feature=${FeatureType.TeamSharedBackpack}
                                >
                            </upgrade-cta>
                        </div>`
                    : ''}
                    <error-toast id="errorToast"></error-toast>
                </div>

            ` : html`<upgrade-dialog
                        .featureType="${FeatureType.AddMapEditors}"
                        @close="${() => this.closeUpgradeComponent()}"
                        >
                    </upgrade-dialog>`
            }
        `;
    }

    static get styles() {
        return css`
            .grantAccessContent {
                width: 550px;
                max-width: 100%;
                box-sizing: border-box;
                padding: 1rem;
            }

            #grantAccessTitle {
                margin-bottom: 1.25rem;
                color: var(--main-primary-color);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            mwc-icon {
                width: 2rem;
            }

            .innerBox {
                border-radius: 0.75rem;
                border: 1px solid var(--theme-border-color);
                padding: 1rem;
                margin-top: 1.25rem;
                max-height: 60%;
                overflow-x: hidden;
                overflow-y: auto;
            }

            .message {
                padding-top: 1.5rem;
            }

            access-item {
                display: block;
                border-bottom: 1px solid var(--theme-border-color);
                padding-bottom: 1rem;
                margin-bottom: 1rem;
            }

            access-item:last-of-type {
                border-bottom: none;
                margin-bottom: 0;
                padding-bottom: 0;
            }

            @media only screen and (max-width: 550px) {
                .grantAccessContent {
                    padding-left: 0;
                    padding-right: 0;
                }
            }
        `;
    }
}
